// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import {
    AccessEvent,
    AccessEventType,
    SearchAccessEventsByEmailAndKeywordPayload,
    SearchAccessEventsPayload,
    StoreAccessEventPayload,
} from "./types/accessEventTypes";
import { RecentActivityResponseDto } from "./types/recentActivityTypes";
import {
    AccessEventUsageReportRequest,
    AccessEventUsageReportResponse,
} from "./types/accessEventReportTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedAccessEventsApi = createApi({
    reducerPath: "cclTokenedAccessEventsApi",
    tagTypes: ["eventTypes", "recentActivity"],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getAccessEventTypes: builder.query<AccessEventType[], void>({
            query: () => "ccl-access2-data/v1.0/AccessEvents/types",
            providesTags: ["eventTypes"],
        }),
        getRecentActivity: builder.query<RecentActivityResponseDto, void>({
            query: () => "ccl-access2-data/v1.0/AccessEvents/recent-activity",
            providesTags: ["recentActivity"],
        }),
        storeAccessEvent: builder.mutation<AccessEvent, Partial<StoreAccessEventPayload>>({
            query: (args) => ({
                url: "ccl-access2-data/v1.0/AccessEvents",
                method: "POST",
                body: args,
            }),
        }),
        getAccessEventsByDate: builder.mutation<AccessEvent[], SearchAccessEventsPayload>({
            query: (args) => ({
                url: "ccl-access2-data/v1.0/AccessEvents/search/by-date",
                method: "POST",
                body: args,
            }),
        }),
        getAccessEventsByKeyword: builder.mutation<AccessEvent[], SearchAccessEventsPayload>({
            query: (args) => ({
                url: "ccl-access2-data/v1.0/AccessEvents/search/by-keyword",
                method: "POST",
                body: args,
            }),
        }),
        getAccessEventsByEmailAndKeyword: builder.mutation<
            AccessEvent[],
            SearchAccessEventsByEmailAndKeywordPayload
        >({
            query: (args) => ({
                url: `ccl-access2-data/v1.0/AccessEvents/search/${encodeURIComponent(
                    args.email,
                )}/by-keyword`,
                method: "POST",
                body: args.payload,
            }),
        }),
        getAccessEventsByEmail: builder.query<AccessEvent[], string>({
            query: (email) =>
                `ccl-access2-data/v1.0/AccessEvents/search/${encodeURIComponent(email)}`,
        }),
        getAccessEventUsageReport: builder.mutation<
            AccessEventUsageReportResponse,
            AccessEventUsageReportRequest
        >({
            query: (args) => ({
                url: "ccl-access2-data/v1.0/AccessEvents/reports/usage",
                method: "POST",
                body: args,
            }),
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetAccessEventTypesQuery,
    useGetRecentActivityQuery,
    useLazyGetRecentActivityQuery,
    useStoreAccessEventMutation,
    useGetAccessEventsByDateMutation,
    useGetAccessEventsByKeywordMutation,
    useGetAccessEventsByEmailAndKeywordMutation,
    useLazyGetAccessEventsByEmailQuery,
    useGetAccessEventUsageReportMutation,
} = cclTokenedAccessEventsApi;
